import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import {
  User as FirebaseUser,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  updateProfile,
  signInAnonymously as firebaseSignInAnonymously,
} from 'firebase/auth';
import { auth, googleProvider } from '../services/firebase';
import { ensureUserDocument, getUserIsAdmin, getUserIsOwner } from '../services/users';

interface AuthContextType {
  currentUser: FirebaseUser | null;
  loading: boolean;
  isAdmin: boolean;
  isOwner: boolean;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInAnonymously: () => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const authInitSequenceRef = useRef(0);

  async function createUserDocument(user: FirebaseUser) {
    await ensureUserDocument(user);
  }

  async function signUp(email: string, password: string, displayName: string) {
    const { user } = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(user, { displayName });
    await createUserDocument(user);
  }

  async function signIn(email: string, password: string) {
    await signInWithEmailAndPassword(auth, email, password);
  }

  async function signInWithGoogle() {
    const { user } = await signInWithPopup(auth, googleProvider);
    await createUserDocument(user);
  }

  async function signInAnonymouslyFn() {
    await firebaseSignInAnonymously(auth);
  }

  async function logout() {
    await signOut(auth);
  }

  async function resetPassword(email: string) {
    await sendPasswordResetEmail(auth, email);
  }

  useEffect(() => {
    let mounted = true;

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const initSequence = authInitSequenceRef.current + 1;
      authInitSequenceRef.current = initSequence;

      const initializeUser = async () => {
        setLoading(true);
        setCurrentUser(user);

        if (!user || user.isAnonymous) {
          if (mounted && authInitSequenceRef.current === initSequence) {
            setIsAdmin(false);
            setIsOwner(false);
            setLoading(false);
          }
          return;
        }

        try {
          await createUserDocument(user);
          const [admin, owner] = await Promise.all([
            getUserIsAdmin(user.uid),
            getUserIsOwner(user.uid),
          ]);

          if (mounted && authInitSequenceRef.current === initSequence) {
            setIsAdmin(admin);
            setIsOwner(owner);
          }
        } catch (err) {
          console.warn('Failed to initialize user:', err);
          if (mounted && authInitSequenceRef.current === initSequence) {
            setIsAdmin(false);
            setIsOwner(false);
          }
        } finally {
          if (mounted && authInitSequenceRef.current === initSequence) {
            setLoading(false);
          }
        }
      };

      void initializeUser();
    });

    return () => {
      mounted = false;
      authInitSequenceRef.current += 1;
      unsubscribe();
    };
  }, []);

  const value: AuthContextType = {
    currentUser,
    loading,
    isAdmin,
    isOwner,
    signUp,
    signIn,
    signInWithGoogle,
    signInAnonymously: signInAnonymouslyFn,
    logout,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
